/* eslint-disable jsx-a11y/img-redundant-alt */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { Pagination } from "@/components/ui/pagination";
import { inputClass } from "@/lib/formStyles";
import { cn } from "@/lib/utils";
import { CREATE_USER } from "api/dataProviderTypes";
import { InsurerRequest } from "api/types";
import ErrorWrapper from "components/Others/ErrorWrapper";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  createUser,
  deleteUser,
  getUsersByRole,
  updateUser,
} from "services/users";
import { roles } from "../../constants/roles";
import { setCurrentInsurer } from "../../redux/users/actions";
import { setStorageInsurer } from "../../utils/storageUtils";
import DashboardWrapper from "../Layout/DashboardWrapper";
import ModalUI from "../legacy/Modal";

const initialsOf = (name?: string) => {
  if (!name) return null;
  const parts = name.trim().split(/\s+/);
  return (
    ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || null
  );
};

// Variante de `inputClass` avec un padding-left adapté pour les champs
// qui portent une icône leading absolue (`absolute left-3.5 …`).
const inputWithIconClass = cn(inputClass, "pl-11");

// Form vide pour la création — sert aussi de reset state à la fermeture
// du modal pour éviter de fuir des valeurs entre deux ouvertures.
const EMPTY_INSURER_FORM: any = {
  roleName: roles.ROLE_INSURER,
  name: "",
  address: "",
  phoneNumber: "",
  email: "",
};

const Assureur = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form, setForm] = useState<any>(EMPTY_INSURER_FORM);
  // editingId === null → mode création ; sinon → édition du insurer.id.
  // Détermine quel endpoint POST/PUT envoyer et l'intitulé du modal.
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce de la recherche pour éviter une rafale d'appels back.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const {
    isLoading,
    data: resultData,
    isFetching,
    refetch,
  } = useQuery(
    ["insurers", debouncedSearch, page],
    () => getUsersByRole(roles.ROLE_INSURER, debouncedSearch, page, pageSize),
    {
      staleTime: 30000,
      keepPreviousData: true,
    }
  );

  const paginatedData: any = resultData?.data || {};
  const users: InsurerRequest[] = paginatedData.content || [];
  const errors = (resultData as any)?.errors;
  const totalPages = paginatedData.totalPages || 0;
  const totalElements = paginatedData.totalElements || 0;
  const currentPage = paginatedData.currentPage || 0;
  const hasNext = paginatedData.hasNext || false;
  const hasPrevious = paginatedData.hasPrevious || false;

  const [isOpen, setIsOpen] = useState(false);
  const [localInsurer, setLocalInsurer] = useState<InsurerRequest>();
  const [openFormModal, setOpenFormModal] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<InsurerRequest | null>(
    null
  );
  const localInsurerInitials = initialsOf(localInsurer?.name);

  // --- Mutations -----------------------------------------------------------
  // `apiRequest` ne throw pas et `notifyApiError` toaste déjà tout `!ok`
  // côté wrapper (cf. utils/apiRequest.js). On garde seulement le contrôle
  // de flux et le toast.success — pas de `toast.error(result.message)` ni
  // de `onError` (mort).

  const { mutate: mutateCreate, isLoading: isCreatingInsurer } = useMutation(
    CREATE_USER,
    () => createUser(form),
    {
      onSuccess: (result: any) => {
        if (!result?.ok) return;
        toast.success("Assureur ajouté avec succès");
        closeFormModal();
        refetch();
      },
    }
  );

  const { mutate: mutateUpdate, isLoading: isUpdatingInsurer } = useMutation(
    () => updateUser({ ...form, id: editingId }),
    {
      onSuccess: (result: any) => {
        if (!result?.ok) return;
        toast.success("Assureur modifié avec succès");
        closeFormModal();
        refetch();
      },
    }
  );

  const { mutate: mutateDelete, isLoading: isDeleting } = useMutation(
    (id: string | number) => deleteUser(id),
    {
      onSuccess: (result: any) => {
        if (!result?.ok) return;
        toast.success("Assureur supprimé");
        setDeleteCandidate(null);
        // Si on supprimait l'assureur actuellement ouvert dans le drawer,
        // fermer aussi le drawer pour éviter de pointer sur un fantôme.
        if (
          localInsurer &&
          (localInsurer as any).id === (deleteCandidate as any)?.id
        ) {
          setIsOpen(false);
          setLocalInsurer(undefined);
        }
        refetch();
      },
    }
  );

  // --- Handlers ------------------------------------------------------------

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_INSURER_FORM);
    setOpenFormModal(true);
  };

  const openEdit = (insurer: any) => {
    setEditingId(insurer?.id ?? null);
    setForm({
      roleName: roles.ROLE_INSURER,
      name: insurer?.name || "",
      address: insurer?.address || "",
      phoneNumber: insurer?.phoneNumber || "",
      email: insurer?.email || "",
    });
    setOpenFormModal(true);
  };

  const closeFormModal = () => {
    setOpenFormModal(false);
    // Reset asynchrone pour éviter le flash visuel des champs qui se
    // vident pendant l'animation de fermeture du modal.
    setTimeout(() => {
      setEditingId(null);
      setForm(EMPTY_INSURER_FORM);
    }, 200);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev: any) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    // Ne pas fermer la modale ici : c'est `onSuccess` qui le fait
    // uniquement quand result.ok est vrai. Fermer avant le retour back
    // perdrait la saisie en cas d'erreur 409 (email/username déjà
    // pris) et l'utilisateur ne verrait pas pourquoi rien ne se passe.
    if (editingId != null) {
      mutateUpdate();
    } else {
      mutateCreate();
    }
  };

  const goToInsurerTarifs = (insurer: InsurerRequest) => {
    dispatch(setCurrentInsurer(insurer));
    setStorageInsurer(insurer);
    navigate("/assureur-details");
  };

  const isEditing = editingId != null;
  const isSavingForm = isCreatingInsurer || isUpdatingInsurer;

  return (
    <DashboardWrapper
      showSpinner={isLoading || isFetching || isSavingForm}
    >
      <>
        {/* Confirmation suppression — pattern aligné sur PersonnelMedecin/Patient. */}
        <ModalUI
          isOpen={deleteCandidate != null}
          onClose={() => {
            if (!isDeleting) setDeleteCandidate(null);
          }}
          styles={{ width: "min(440px, 92vw)", padding: 0 }}
        >
          <div className="flex flex-col">
            <div className="flex items-start gap-4 px-6 pt-6">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600 ring-1 ring-rose-100">
                <i className="bx bx-trash text-2xl" />
              </div>
              <div className="space-y-1">
                <p className="text-[11px] uppercase tracking-[0.32em] text-rose-600 font-semibold">
                  Action irréversible
                </p>
                <h2 className="text-lg font-semibold text-slate-900 leading-tight">
                  Supprimer cet assureur ?
                </h2>
                <p className="text-sm text-slate-500">
                  Le partenariat avec{" "}
                  <span className="font-medium text-slate-800">
                    {deleteCandidate?.name}
                  </span>{" "}
                  sera supprimé définitivement, ainsi que ses tarifs négociés.
                  Cette action ne peut pas être annulée.
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
              <button
                type="button"
                onClick={() => setDeleteCandidate(null)}
                disabled={isDeleting}
                className="flex-1 inline-flex items-center justify-center h-11 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors disabled:opacity-60"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => {
                  if ((deleteCandidate as any)?.id != null) {
                    mutateDelete((deleteCandidate as any).id);
                  }
                }}
                disabled={isDeleting || (deleteCandidate as any)?.id == null}
                className="flex-[1.4] inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white text-sm font-medium shadow-lg shadow-rose-600/25 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isDeleting ? (
                  <>
                    <i className="bx bx-loader-alt animate-spin text-base" />
                    <span>Suppression…</span>
                  </>
                ) : (
                  <>
                    <i className="bx bx-trash text-base" />
                    <span>Supprimer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </ModalUI>

        {/* Drawer profil assureur */}
        <ModalUI
          isOpen={isOpen}
          styles={{
            top: 0,
            left: "unset",
            transform: "unset",
            right: 0,
            height: "100%",
            width: "26rem",
            maxWidth: "92vw",
            borderRadius: 0,
            padding: 0,
          }}
          onClose={() => setIsOpen(false)}
        >
          <div className="h-full flex flex-col">
            <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-start justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                  Profil assureur
                </p>
                <h2 className="mt-1 text-lg font-semibold text-slate-800">
                  Détails
                </h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                aria-label="Fermer"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
              >
                <i className="bx bx-x text-xl" />
              </button>
            </div>

            <div className="flex-1 overflow-auto px-6 py-6 space-y-6">
              {/* Carte identité */}
              <div className="flex flex-col items-center text-center">
                <div className="relative">
                  <span className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white text-2xl font-semibold ring-4 ring-white shadow-lg">
                    {localInsurerInitials || (
                      <i className="bx bx-shield-alt-2" />
                    )}
                  </span>
                  <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Partenaire actif
                  </span>
                </div>
                <h3 className="mt-6 text-slate-900 text-lg font-semibold">
                  {localInsurer?.name}
                </h3>
              </div>

              {/* Carte coordonnées */}
              <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <header className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
                  <i className="bx bx-id-card text-lg text-emerald-600" />
                  <h5 className="text-sm font-semibold text-slate-800">
                    Coordonnées
                  </h5>
                </header>
                <dl className="divide-y divide-slate-100">
                  <div className="px-4 py-3 flex items-start gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                      <i className="bx bx-envelope text-base" />
                    </span>
                    <div className="min-w-0">
                      <dt className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                        Email
                      </dt>
                      <dd className="mt-0.5 text-sm text-slate-700 break-all">
                        {localInsurer?.email || (
                          <span className="text-slate-400 italic">
                            Non renseigné
                          </span>
                        )}
                      </dd>
                    </div>
                  </div>
                  <div className="px-4 py-3 flex items-start gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                      <i className="bx bx-phone text-base" />
                    </span>
                    <div className="min-w-0">
                      <dt className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                        Téléphone
                      </dt>
                      <dd className="mt-0.5 text-sm text-slate-700">
                        {localInsurer?.phoneNumber || (
                          <span className="text-slate-400 italic">
                            Non renseigné
                          </span>
                        )}
                      </dd>
                    </div>
                  </div>
                  <div className="px-4 py-3 flex items-start gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                      <i className="bx bx-map-pin text-base" />
                    </span>
                    <div className="min-w-0">
                      <dt className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
                        Adresse
                      </dt>
                      <dd className="mt-0.5 text-sm text-slate-700">
                        {localInsurer?.address || (
                          <span className="text-slate-400 italic">
                            Non renseignée
                          </span>
                        )}
                      </dd>
                    </div>
                  </div>
                </dl>
              </section>

              {/* CTA : voir les tarifs négociés */}
              <button
                type="button"
                onClick={() => {
                  if (localInsurer) {
                    setIsOpen(false);
                    goToInsurerTarifs(localInsurer);
                  }
                }}
                className="w-full inline-flex items-center justify-between gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-sm font-medium text-slate-700 hover:bg-emerald-50/40 hover:border-emerald-200 hover:text-emerald-700 transition-colors group"
              >
                <span className="flex items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                    <i className="bx bx-list-check text-lg" />
                  </span>
                  <span>
                    Voir les tarifs négociés
                    <span className="block text-xs text-slate-400 font-normal mt-0.5">
                      Catalogue des actes & prix
                    </span>
                  </span>
                </span>
                <i className="bx bx-chevron-right text-xl text-slate-400 group-hover:text-emerald-600 transition-colors" />
              </button>
            </div>

            {/* Footer actions */}
            <div className="shrink-0 border-t border-slate-100 bg-slate-50/50 px-6 py-4 flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setDeleteCandidate(localInsurer as any);
                }}
                className="inline-flex items-center justify-center gap-2 h-11 rounded-xl border border-rose-200 bg-white text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors px-4"
              >
                <i className="bx bx-trash text-base" />
                Supprimer
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  openEdit(localInsurer);
                }}
                className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-green-600 hover:bg-green-700 active:bg-green-800 text-white text-sm font-medium shadow-lg shadow-green-600/25 transition-all duration-300"
              >
                <i className="bx bx-edit-alt text-base" />
                Modifier
              </button>
            </div>
          </div>
        </ModalUI>

        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both">
          {/* Hero */}
          <header className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                  Partenaires assurance
                </p>
                <h1 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                  Assureurs
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  {totalElements} compagnie{totalElements > 1 ? "s" : ""} active
                  {totalElements > 1 ? "s" : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={openCreate}
                className="group/btn relative inline-flex items-center gap-2 overflow-hidden self-start sm:self-end rounded-xl bg-green-600 hover:bg-green-700 active:bg-green-800 text-white text-sm font-medium px-5 py-3 shadow-lg shadow-green-600/25 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0 -left-full w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-all duration-700 group-hover/btn:left-full"
                />
                <i className="bx bx-shield-alt-2 text-lg" />
                Nouveau partenaire
              </button>
            </div>
          </header>

          {/* Search bar */}
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-500 delay-100 fill-mode-both">
            <label className="relative block w-full md:w-96">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none">
                <i className="bx bx-search" />
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={cn(inputClass, "pl-11 mt-0")}
                placeholder="Rechercher un assureur…"
              />
            </label>
          </div>

          {/* Table */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500 delay-200 fill-mode-both">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/70 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                    <th className="px-4 py-3 text-left">Assureur</th>
                    <th className="px-4 py-3 text-left">Adresse</th>
                    <th className="px-4 py-3 text-left">Téléphone</th>
                    <th className="px-4 py-3 text-left">E-mail</th>
                    <th className="px-4 py-3 text-right" />
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-16 text-center">
                        <i className="bx bx-shield-alt-2 text-4xl text-slate-300" />
                        <p className="mt-2 text-sm font-medium text-slate-600">
                          Aucun assureur
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                          Ajustez votre recherche ou ajoutez un partenaire.
                        </p>
                      </td>
                    </tr>
                  ) : (
                    users.map((user: any, idx) => {
                      const initials = initialsOf(user?.name);
                      return (
                        <tr
                          key={user?.id ?? idx}
                          onClick={() => goToInsurerTarifs(user)}
                          // Accessibilité : la ligne agit comme un lien.
                          // role="link" + tabIndex permet la navigation clavier
                          // (Enter / Space) ; les boutons d'action enfants
                          // appellent `e.stopPropagation()` pour ne pas
                          // déclencher cette navigation à leur clic.
                          role="link"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              goToInsurerTarifs(user);
                            }
                          }}
                          className="group/row cursor-pointer border-t border-slate-100 hover:bg-emerald-50/40 transition-colors focus:outline-none focus:bg-emerald-50/60 focus:ring-2 focus:ring-emerald-200 focus:ring-inset"
                        >
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-sm font-semibold text-white ring-2 ring-white shadow-sm">
                                {initials || (
                                  <i className="bx bx-shield-alt-2" />
                                )}
                              </span>
                              <p className="text-sm font-semibold text-slate-800 truncate">
                                {user?.name || "—"}
                              </p>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600 whitespace-nowrap">
                            {user?.address || (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600 whitespace-nowrap">
                            {user?.phoneNumber || (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600 whitespace-nowrap">
                            {user?.email || (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right">
                            <div className="inline-flex items-center gap-1 opacity-70 group-hover/row:opacity-100 transition-opacity">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setLocalInsurer(user);
                                  setIsOpen(true);
                                }}
                                className="inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-400 hover:bg-emerald-50 hover:text-emerald-700 transition"
                                aria-label="Voir le profil"
                                title="Voir le profil"
                              >
                                <i className="bx bx-show text-lg" />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEdit(user);
                                }}
                                className="inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-400 hover:bg-amber-50 hover:text-amber-600 transition"
                                aria-label="Modifier"
                                title="Modifier"
                              >
                                <i className="bx bx-edit-alt text-lg" />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteCandidate(user);
                                }}
                                className="inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition"
                                aria-label="Supprimer"
                                title="Supprimer"
                              >
                                <i className="bx bx-trash text-lg" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300 fill-mode-both">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalElements={totalElements}
              currentCount={users.length}
              hasNext={hasNext}
              hasPrevious={hasPrevious}
              onPageChange={setPage}
              itemLabel="assureur"
              isLoading={isLoading}
            />
          </div>

          {/* Modal — création / édition (modal partagé, mode-aware via editingId) */}
          <ModalUI
            isOpen={openFormModal}
            onClose={() => {
              if (!isSavingForm) closeFormModal();
            }}
            styles={{ height: "80%", padding: 0 }}
          >
            <form className="h-full flex flex-col">
              {/* Header */}
              <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-slate-100 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                    {isEditing ? "Modifier l'assureur" : "Nouveau partenaire"}
                  </p>
                  <h2 className="mt-1 text-lg font-semibold text-slate-800 truncate">
                    {isEditing
                      ? form.name || "Modifier la fiche"
                      : "Enregistrer un assureur"}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!isSavingForm) closeFormModal();
                  }}
                  aria-label="Fermer"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
                >
                  <i className="bx bx-x text-xl" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 sm:px-8 py-6 flex-1 overflow-auto space-y-8">
                <ErrorWrapper errors={errors} />

                {/* Section Identité */}
                <section className="space-y-4">
                  <div className="flex items-center gap-2">
                    <i className="bx bx-shield-alt-2 text-base text-emerald-600" />
                    <h3 className="text-sm font-semibold text-slate-700">
                      Identité
                    </h3>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>
                  <label className="block">
                    <span className="block text-sm font-medium text-slate-700">
                      Raison sociale
                    </span>
                    <div className="relative">
                      <i className="bx bx-buildings absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
                      <input
                        type="text"
                        name="name"
                        value={form.name || ""}
                        onChange={handleChange}
                        placeholder="Nom de la compagnie"
                        className={inputWithIconClass}
                      />
                    </div>
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-slate-700">
                      Adresse
                    </span>
                    <div className="relative">
                      <i className="bx bx-map-pin absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
                      <input
                        type="text"
                        name="address"
                        value={form.address || ""}
                        onChange={handleChange}
                        placeholder="Ville, quartier"
                        className={inputWithIconClass}
                      />
                    </div>
                  </label>
                </section>

                {/* Section Coordonnées */}
                <section className="space-y-4">
                  <div className="flex items-center gap-2">
                    <i className="bx bx-envelope text-base text-emerald-600" />
                    <h3 className="text-sm font-semibold text-slate-700">
                      Coordonnées
                    </h3>
                    <div className="flex-1 h-px bg-slate-100" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="block text-sm font-medium text-slate-700">
                        Téléphone
                      </span>
                      <div className="relative">
                        <i className="bx bx-phone absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
                        <input
                          type="text"
                          name="phoneNumber"
                          value={form.phoneNumber || ""}
                          onChange={handleChange}
                          placeholder="(+229) …"
                          className={inputWithIconClass}
                        />
                      </div>
                    </label>
                    <label className="block">
                      <span className="block text-sm font-medium text-slate-700">
                        Email
                      </span>
                      <div className="relative">
                        <i className="bx bx-envelope absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
                        <input
                          type="email"
                          name="email"
                          value={form.email || ""}
                          onChange={handleChange}
                          placeholder="contact@assureur.com"
                          className={inputWithIconClass}
                        />
                      </div>
                    </label>
                  </div>
                </section>
              </div>

              {/* Footer */}
              <div className="px-6 sm:px-8 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (!isSavingForm) closeFormModal();
                  }}
                  disabled={isSavingForm}
                  className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition disabled:opacity-60"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSavingForm}
                  className="inline-flex items-center gap-2 rounded-xl bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:opacity-70 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2.5 shadow-lg shadow-green-600/25 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
                >
                  {isSavingForm ? (
                    <>
                      <i className="bx bx-loader-alt animate-spin text-base" />
                      <span>
                        {isEditing ? "Enregistrement…" : "Création…"}
                      </span>
                    </>
                  ) : (
                    <>
                      <i
                        className={
                          isEditing ? "bx bx-save text-lg" : "bx bx-check text-lg"
                        }
                      />
                      <span>{isEditing ? "Enregistrer" : "Créer"}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </ModalUI>
        </div>
      </>
    </DashboardWrapper>
  );
};

export default Assureur;
